import React, { useRef, useState } from "react";
import { RotateCcw, Download } from "lucide-react";
import { KOLAM_COLOR_REGIONS, KOLAM_PALETTE } from "../../data/studio";
import ShareArtwork from "./ShareArtwork";

export default function ColorKolam({ onSaved }) {
  const svgRef = useRef(null);
  const [activeColor, setActiveColor] = useState(KOLAM_PALETTE[1].value);
  const [fills, setFills] = useState({});

  const handleRegionClick = (id) => {
    setFills((prev) => ({ ...prev, [id]: activeColor }));
  };

  const reset = () => setFills({});

  const downloadColor = async () => {
    const svg = svgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const dataUrl = `data:image/svg+xml;base64,${svg64}`;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const out = document.createElement("canvas");
      out.width = 800;
      out.height = 800;
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#F9F6F0";
      ctx.fillRect(0, 0, 800, 800);
      ctx.drawImage(img, 0, 0, 800, 800);
      const link = document.createElement("a");
      link.download = "my-kolam-coloring.png";
      link.href = out.toDataURL("image/png");
      link.click();
      if (onSaved) onSaved();
    };
    img.src = dataUrl;
  };

  return (
    <div data-testid="color-kolam" className="grid lg:grid-cols-12 gap-8 items-start">
      <div className="lg:col-span-8">
        <div className="relative w-full aspect-square rounded-2xl overflow-hidden border-4 border-[#E5DFD3] bg-[#F9F6F0] shadow-xl">
          <svg
            ref={svgRef}
            viewBox="0 0 400 400"
            xmlns="http://www.w3.org/2000/svg"
            className="absolute inset-0 w-full h-full"
            data-testid="color-svg"
          >
            {/* Background grid of faint dots for kolam feel */}
            {[80, 160, 240, 320].map((y) =>
              [80, 160, 240, 320].map((x) => (
                <circle key={`bg-${x}-${y}`} cx={x} cy={y} r="1.5" fill="#E5DFD3" />
              ))
            )}
            {KOLAM_COLOR_REGIONS.map((r) => (
              <path
                key={r.id}
                d={r.d}
                fill={fills[r.id] || "#FFFFFF"}
                stroke="#1E2A4F"
                strokeWidth="2"
                strokeLinejoin="round"
                onClick={() => handleRegionClick(r.id)}
                data-testid={`color-region-${r.id}`}
                style={{ cursor: "pointer", transition: "fill 0.3s ease" }}
              />
            ))}
          </svg>
        </div>
        <p className="mt-4 text-sm text-[#5C6A8F] text-center">
          Tip: Tap any petal or dot to fill it with your colour. Try a different one to change it.
        </p>
      </div>

      <div className="lg:col-span-4 space-y-6">
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">Pick a colour</p>
          <div className="grid grid-cols-5 gap-2">
            {KOLAM_PALETTE.map((c) => (
              <button
                key={c.value}
                data-testid={`color-swatch-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActiveColor(c.value)}
                aria-label={c.name}
                style={{ background: c.value }}
                className={`aspect-square rounded-lg border-2 transition-transform duration-300 ${
                  activeColor === c.value
                    ? "scale-110 border-[#1E2A4F] ring-2 ring-[#1E2A4F]/30 ring-offset-2 ring-offset-[#F9F6F0]"
                    : "border-[#E5DFD3] hover:scale-105"
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-[#5C6A8F] mt-3">
            Selected: <span className="font-medium text-[#1E2A4F]">{KOLAM_PALETTE.find((c) => c.value === activeColor)?.name}</span>
          </p>
        </div>

        <button
          data-testid="color-reset"
          onClick={reset}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-sm"
        >
          <RotateCcw size={14} /> Start over
        </button>

        <button
          data-testid="color-download"
          onClick={downloadColor}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full bg-[#D95D39] text-[#F9F6F0] hover:bg-[#1E2A4F] transition-colors duration-300 text-sm font-medium tracking-wide"
        >
          <Download size={16} /> Save my kolam art
        </button>
        <ShareArtwork
          artFormId="kolam"
          artName="Kolam"
          accent="#D95D39"
          testIdPrefix="color-kolam"
        />
      </div>
    </div>
  );
}
